import { createContext } from 'react';
import {
	ICreateModifierGroupValidationError,
	IItemForGetShortInfo,
	IServerError,
	TLanguageIDs,
	IMenuTranslation,
	IDefaultLanguage,
	IModifiersForModifierGroupsInfo,
	IUpdateModifierGroups,
	IDishesByRestaurantIdsAndSubcategoryIdsItem,
} from 'types';

export type TVisibleForm = 'mainForm' | 'translations';
export type TTypeOfModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'defaultTranslationIsEmpty';
export interface IInitialFormData {
	IDSelectedCustomer: Nullable<string>;
	IDsSelectedRestaurant: string[];
	IDsIsForceDisabledRestaurant: string[];
	IDsSelectedDishes: string[];
	nameOfModifierGroup: string;
	externalId: string;
	translations: IMenuTranslation[];
}

export interface IEditModifierGroupsState {
	needRefetchData: boolean;
	initialFormData: IInitialFormData | null;
	isLoading: boolean;
	modifierGroupsID: string;
	visibleForm: TVisibleForm;
	fetchedCustomers: IItemForGetShortInfo[];
	initialIDSelectedCustomer: Nullable<string>;
	IDSelectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfo[];
	initialIDsSelectedRestaurant: string[];
	IDsSelectedRestaurant: string[];
	IDsIsForceDisabledRestaurant: string[];
	fetchedCategories: IItemForGetShortInfo[];
	initialIDsSelectedCategories: string[];
	IDsSelectedCategories: string[];
	fetchedSubcategories: IItemForGetShortInfo[];
	initialIDsSelectedSubcategories: string[];
	IDsSelectedSubcategories: string[];
	fetchedDishes: IDishesByRestaurantIdsAndSubcategoryIdsItem[];
	initialIDsSelectedDishes: string[];
	IDsSelectedDishes: string[];
	nameOfModifierGroup: string;
	externalId: string;
	articleNumber: Nullable<string>;
	modifiersOfModifierGroups: IModifiersForModifierGroupsInfo[];
	defaultCustomerLanguage: Nullable<IDefaultLanguage>;
	selectedTranslationTab: TLanguageIDs | null;
	translations: IMenuTranslation[];
	dataForSubmit: IUpdateModifierGroups | null;
	typeOfModalWindow: TTypeOfModalWindow;
	errorResponseData: IServerError<ICreateModifierGroupValidationError> | null;
}

export const editModifierGroupsInitialState: IEditModifierGroupsState = {
	needRefetchData: false,
	initialFormData: null,
	isLoading: true,
	modifierGroupsID: '',
	visibleForm: 'mainForm',
	fetchedCustomers: [],
	initialIDSelectedCustomer: null,
	IDSelectedCustomer: null,
	fetchedRestaurants: [],
	initialIDsSelectedRestaurant: [],
	IDsSelectedRestaurant: [],
	IDsIsForceDisabledRestaurant: [],
	fetchedCategories: [],
	initialIDsSelectedCategories: [],
	IDsSelectedCategories: [],
	fetchedSubcategories: [],
	initialIDsSelectedSubcategories: [],
	IDsSelectedSubcategories: [],
	fetchedDishes: [],
	initialIDsSelectedDishes: [],
	IDsSelectedDishes: [],
	nameOfModifierGroup: '',
	externalId: '',
	articleNumber: null,
	modifiersOfModifierGroups: [],
	defaultCustomerLanguage: null,
	selectedTranslationTab: null,
	translations: [],
	dataForSubmit: null,
	typeOfModalWindow: null,
	errorResponseData: null,
};

export interface IEditModifierGroupsContext extends IEditModifierGroupsState {
	refetchData(value: boolean): void;
	changeLoaderStatus(value: boolean): void;
	changeVisibleForm(value: TVisibleForm): void;
	setFetchedCustomers(value: IItemForGetShortInfo[]): void;
	setIDCustomer(value: Nullable<string>): void;
	setFetchedRestaurants(value: IItemForGetShortInfo[]): void;
	setIDsRestaurant(value: string[]): void;
	setIDsIsForceDisabledRestaurant(value: string): void;
	setFetchedCategories(value: IItemForGetShortInfo[]): void;
	setIDsCategories(value: string[]): void;
	updateCategories(value: IItemForGetShortInfo[]): void;
	setFetchedSubcategories(value: IItemForGetShortInfo[]): void;
	setIDsSubcategories(value: string[]): void;
	updateSubcategories(value: IItemForGetShortInfo[]): void;
	setFetchedDishes(value: IDishesByRestaurantIdsAndSubcategoryIdsItem[]): void;
	setIDsDishes(value: string[]): void;
	updateDishes(value: IDishesByRestaurantIdsAndSubcategoryIdsItem[]): void;
	changeNameOfModifierGroup(value: string): void;
	changeExternalId(value: string): void;
	generateUniqueExternalId(): void;
	setDefaultCustomerLanguage(value: Nullable<IDefaultLanguage>): void;
	setTranslationTab(value: TLanguageIDs | null): void;
	changeNameTranslation(languageId: TLanguageIDs, value: string): void;
	setDataForSubmit(value: IUpdateModifierGroups): void;
	changeTypeOfModalWindow(value: TTypeOfModalWindow): void;
	showErrorModalWithInfo(value: IServerError<ICreateModifierGroupValidationError> | null): void;
	resetFields(): void;
}

export const EditModifierGroupsContext = createContext<IEditModifierGroupsContext>({
	refetchData: () => undefined,
	changeLoaderStatus: () => undefined,
	changeVisibleForm: () => undefined,
	setFetchedCustomers: () => undefined,
	setIDCustomer: () => undefined,
	setFetchedRestaurants: () => undefined,
	setIDsRestaurant: () => undefined,
	setIDsIsForceDisabledRestaurant: () => undefined,
	setFetchedCategories: () => undefined,
	setIDsCategories: () => undefined,
	updateCategories: () => undefined,
	setFetchedSubcategories: () => undefined,
	setIDsSubcategories: () => undefined,
	updateSubcategories: () => undefined,
	setFetchedDishes: () => undefined,
	setIDsDishes: () => undefined,
	updateDishes: () => undefined,
	changeNameOfModifierGroup: () => undefined,
	changeExternalId: () => undefined,
	generateUniqueExternalId: () => undefined,
	setDefaultCustomerLanguage: () => undefined,
	setTranslationTab: () => undefined,
	changeNameTranslation: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	showErrorModalWithInfo: () => undefined,
	resetFields: () => undefined,
	...editModifierGroupsInitialState,
});
